// src/features/auth/context/AuthContext.tsx
import React, {
    createContext,
    useContext,
    useEffect,
    useState,
    useCallback,
    useRef,
} from "react";

import { authApi } from "../api/auth.api";
import { UserProfile } from "../types";
import {
    AUTH_SESSION_EXPIRED_EVENT,
    refreshManager,
} from "../../../core/http/refresh.manager";

type SessionStatus =
    | "initializing"
    | "authenticated"
    | "unauthenticated";

interface AuthContextType {
    user: UserProfile | null;
    status: SessionStatus;
    isAuthenticated: boolean;

    login: (data: any) => Promise<void>;
    register: (data: any) => Promise<void>;
    logout: () => Promise<void>;

    initSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface Props {
    children: React.ReactNode;
}

export const AuthProvider = ({ children }: Props) => {
    const [user, setUser] = useState<UserProfile | null>(null);
    const [status, setStatus] = useState<SessionStatus>("initializing");

    const isAuthenticated = status === "authenticated";

    const initRef = useRef(false);
    const loadingRef = useRef(false);

    /**
     * =========================
     * INITIALISATION SESSION
     * =========================
     */
    const initSession = useCallback(async () => {
        if (loadingRef.current) return;

        loadingRef.current = true;
        setStatus("initializing");

        try {
            const response = await authApi.me();
            const currentUser = response.data?.user ?? null;

            setUser(currentUser);
            setStatus(currentUser ? "authenticated" : "unauthenticated");
        } catch {
            setUser(null);
            setStatus("unauthenticated");
        } finally {
            loadingRef.current = false;
        }
    }, []);

    /**
     * =========================
     * LOGIN
     * =========================
     */
    const login = useCallback(async (payload: any) => {
        await authApi.login(payload);

        const response = await authApi.me();
        const currentUser = response.data?.user ?? null;

        setUser(currentUser);
        setStatus(currentUser ? "authenticated" : "unauthenticated");
    }, []);

    /**
     * =========================
     * REGISTER (+ AUTO-LOGIN)
     * =========================
     * L'API crée le user ET retourne les cookies (auto-login).
     * On recharge donc /me pour récupérer le profil.
     */
    const register = useCallback(async (payload: any) => {
        await authApi.register(payload);

        // ✅ L'API a posé les cookies → on est connecté
        try {
            const response = await authApi.me();
            const currentUser = response.data?.user ?? null;

            setUser(currentUser);
            setStatus(currentUser ? "authenticated" : "unauthenticated");
        } catch {
            // Fallback : si /me échoue juste après register
            setUser(null);
            setStatus("unauthenticated");
        }
    }, []);

    /**
     * =========================
     * LOGOUT
     * =========================
     */
    const logout = useCallback(async () => {
        try {
            await authApi.logout();
        } catch {
            // Ignore
        } finally {
            refreshManager.reset();
            setUser(null);
            setStatus("unauthenticated");
        }
    }, []);

    /**
     * =========================
     * BOOTSTRAP
     * =========================
     */
    useEffect(() => {
        if (initRef.current) return;
        initRef.current = true;

        void initSession();
    }, [initSession]);

    /**
     * =========================
     * ÉCOUTE SESSION EXPIRÉE
     * =========================
     */
    useEffect(() => {
        const handleExpired = () => {
            console.warn("[Auth] Session expired globally");
            refreshManager.reset();
            setUser(null);
            setStatus("unauthenticated");
        };

        window.addEventListener(
            AUTH_SESSION_EXPIRED_EVENT,
            handleExpired,
        );

        return () => {
            window.removeEventListener(
                AUTH_SESSION_EXPIRED_EVENT,
                handleExpired,
            );
        };
    }, []);

    /**
     * =========================
     * REFRESH AU FOCUS
     * =========================
     */
    useEffect(() => {
        const handleFocus = () => {
            if (!isAuthenticated) return;

            void authApi
                .me()
                .then((response) => {
                    const currentUser = response.data?.user ?? null;
                    if (currentUser) setUser(currentUser);
                })
                .catch(() => {
                    // L'interceptor gère le refresh ou l'event expired
                });
        };

        window.addEventListener("focus", handleFocus);

        return () => {
            window.removeEventListener("focus", handleFocus);
        };
    }, [isAuthenticated]);

    return (
        <AuthContext.Provider
            value={{
                user,
                status,
                isAuthenticated,
                login,
                register,
                logout,
                initSession,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);

    if (!context) {
        throw new Error("useAuth must be used within AuthProvider");
    }

    return context;
};